const TRACKER_DEBOUNCE_MS = 400;
const TRACKER_ENDPOINTS: Record<TrackerField, string> = {
    [TrackerField.HitPoints]: "/Participant/SetHitPoints",
    [TrackerField.MaxHitPoints]: "/Participant/SetMaxHitPoints",
    [TrackerField.ArmorPoints]: "/Participant/SetArmorPoints",
    [TrackerField.ExperiencePoints]: "/Participant/SetExperiencePoints"
};
const TRACKER_FIELD_INPUTS: Record<TrackerField, string> = {
    [TrackerField.HitPoints]: "hitPointsInput",
    [TrackerField.MaxHitPoints]: "maxHitPointsInput",
    [TrackerField.ArmorPoints]: "armorPointsInput",
    [TrackerField.ExperiencePoints]: "experiencePointsInput"
};

function InitTrackers(nameSluggified: string): void {
    for (const field of Object.values(TrackerField)) {
        const input = TryById<HTMLInputElement>(TRACKER_FIELD_INPUTS[field]);
        if (!input) {
            continue;
        }

        input.oninput = () => {
            QueueTrackerUpdate(field, nameSluggified, input.value);
            RenderOwnHitPointControls();
        };
    }

    RenderOwnHitPointControls();
}

// Current hit points only mean something against a maximum, and plus has nowhere to go once it is reached.
function RenderHitPointControls(controls: HitPointControls, max: number | null): void {
    controls.current.disabled = max === null;
    controls.minus.disabled = max === null;
    controls.plus.disabled = max === null || (controls.current.value !== "" && Number(controls.current.value) >= max);
}

function RenderOwnHitPointControls(): void {
    const current = TryById<HTMLInputElement>(TRACKER_FIELD_INPUTS[TrackerField.HitPoints]);
    if (!current) {
        return;
    }

    RenderHitPointControls({
        current: current,
        minus: ById<HTMLButtonElement>("hitPointsMinus"),
        plus: ById<HTMLButtonElement>("hitPointsPlus")
    }, TrackerNumber(TrackerField.MaxHitPoints));
}

function RenderContextHitPointControls(): void {
    RenderHitPointControls({
        current: ById<HTMLInputElement>("contextHitPoints"),
        minus: ById<HTMLButtonElement>("contextHitPointsMinus"),
        plus: ById<HTMLButtonElement>("contextHitPointsPlus")
    }, ContextMaxHitPoints());
}

// An empty field posts an empty value, which the server stores as "not tracking" so the tracker disappears
// again for whoever never wanted one in the first place.
function QueueTrackerUpdate(field: TrackerField, nameSluggified: string, value: string): void {
    const key = TrackerKey(field, nameSluggified);
    HoldWrite(key);

    Debounce(key, TRACKER_DEBOUNCE_MS, function () {
        const data: Record<string, string> = { slugName: nameSluggified, roomKey: RoomKey() };
        data[field] = value;

        PostRoundWrite(key, TRACKER_ENDPOINTS[field], data);
    });
}

function StepHitPoints(delta: number): void {
    const max = TrackerNumber(TrackerField.MaxHitPoints);
    if (max === null) {
        return;
    }

    StepTracker(TrackerField.HitPoints, delta, max);
    RenderOwnHitPointControls();
}

function StepArmorPoints(delta: number): void {
    StepTracker(TrackerField.ArmorPoints, delta, null);
}

function StepExperiencePoints(delta: number): void {
    StepTracker(TrackerField.ExperiencePoints, delta, null);
}

function StepTracker(field: TrackerField, delta: number, max: number | null): void {
    const input = ById<HTMLInputElement>(TRACKER_FIELD_INPUTS[field]);

    // Nobody is on the tracker until a number is entered, so counting down from empty would put you on it
    // at zero instead of leaving you untracked.
    if (input.value === "" && delta < 0) {
        return;
    }

    const next = Math.max(0, (input.value === "" ? 0 : Number(input.value)) + delta);
    input.value = String(max === null ? next : Math.min(max, next));

    QueueTrackerUpdate(field, ParticipantName(), input.value);
}

function TrackerNumber(field: TrackerField): number | null {
    const input = ById<HTMLInputElement>(TRACKER_FIELD_INPUTS[field]);
    if (input.value === "" || Number(input.value) <= 0) {
        return null;
    }

    return Number(input.value);
}

// Your own fields follow the room rather than only being seeded once, so a dungeon master who knocks you
// out sees your hit points drop to zero on your own screen too. Whatever you are typing in stays yours.
function RenderTrackers(participants: Participant[]): void {
    const nameSluggified = ById<HTMLInputElement>("participantName").value;
    const self = participants.find(x => x.nameSluggified === nameSluggified);
    if (!self) {
        return;
    }

    for (const field of Object.values(TrackerField)) {
        SyncTrackerField(field, nameSluggified, self[field]);
    }

    RenderOwnHitPointControls();
}

function SyncTrackerField(field: TrackerField, nameSluggified: string, serverValue: number | null): void {
    const input = TryById<HTMLInputElement>(TRACKER_FIELD_INPUTS[field]);
    if (!input || document.activeElement === input) {
        return;
    }

    const serverText = TrackerText(serverValue);
    if (input.value !== serverText) {
        input.value = serverText;
    }
}

function TrackerText(value: number | string | null | undefined): string {
    return value !== null && value !== undefined && value !== "" ? String(value) : "";
}

function HasTrackerValue<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

function RenderContextTrackers(participant: Participant): void {
    SetContextValue("contextHitPoints", participant.hitPoints);
    SetContextValue("contextMaxHitPoints", participant.maxHitPoints);
    SetContextValue("contextExperiencePoints", participant.experiencePoints);
    SetContextValue("contextArmorPoints", participant.armorPoints);
    RenderContextHitPointControls();
    RenderContextDeathToggle(participant.isDead, CanManage(participant));
}

// The dungeon master can manage anyone; everyone else only their own row and the summons under it, and only
// here in the popup rather than as a button standing open on the page.
function CanManage(owner: Participant): boolean {
    return ParticipantRole() === Role.DungeonMaster || owner.nameSluggified === ParticipantName();
}

function RenderContextDeathToggle(isDead: boolean, visible: boolean): void {
    const deathToggle = ById<HTMLElement>("contextDeathToggle");
    deathToggle.dataset.dead = String(isDead);
    deathToggle.style.display = visible ? "block" : "none";
    RenderDeathToggle();
}

function SetContextValue(elementId: string, value: number | string | null): void {
    ById<HTMLInputElement>(elementId).value = HasTrackerValue(value) ? String(value) : "";
}

function RenderDeathToggle(): void {
    const deathToggle = ById<HTMLElement>("contextDeathToggle");
    deathToggle.innerText = IsContextParticipantDead() ? "❤️ Maak levend" : "💀 Maak dood";
}

function IsContextParticipantDead(): boolean {
    return ById<HTMLElement>("contextDeathToggle").dataset.dead === "true";
}

function ContextMaxHitPoints(): number | null {
    const maxInput = ById<HTMLInputElement>("contextMaxHitPoints");
    if (maxInput.value === "" || Number(maxInput.value) <= 0) {
        return null;
    }

    return Number(maxInput.value);
}

function AdjustHitPoints(delta: number): void {
    const hitPointsInput = ById<HTMLInputElement>("contextHitPoints");
    const max = ContextMaxHitPoints();
    if (max === null || (hitPointsInput.value === "" && delta < 0)) {
        return;
    }

    const current = hitPointsInput.value === "" ? 0 : Number(hitPointsInput.value);
    hitPointsInput.value = String(Math.min(max, Math.max(0, current + delta)));
    PostContextHitPoints();
    RenderContextHitPointControls();
}

// Death is only ever this toggle - reaching zero hit points on its own leaves someone in the initiative
// order.
function ToggleDeath(): void {
    const deathToggle = ById<HTMLElement>("contextDeathToggle");
    const isDead = !IsContextParticipantDead();

    deathToggle.dataset.dead = String(isDead);
    RenderDeathToggle();
    PostContextDead(isDead);

    ById<HTMLElement>("applyConditionDropdown").classList.remove("show");
}

function ContextHitPointsChanged(): void {
    PostContextHitPoints();
    RenderContextHitPointControls();
}

// Mirrors the server's full-health rule: the menu is filled once on open and does not follow the round.
function ContextMaxHitPointsChanged(): void {
    const max = ContextMaxHitPoints();
    const hitPointsInput = ById<HTMLInputElement>("contextHitPoints");
    const isDeadStandee = contextEnemyId !== null && hitPointsInput.value === "0";
    if (max !== null && !isDeadStandee) {
        hitPointsInput.value = String(max);
    }

    PostContextMaxHitPoints();
    RenderContextHitPointControls();
}

function PostContextMaxHitPoints(): void {
    if (contextEnemyId !== null) {
        PostContextEnemyField(EnemyField.MaxHitPoints, "contextMaxHitPoints");
        return;
    }

    PostContextTracker(TrackerField.MaxHitPoints, "contextMaxHitPoints");
}

function PostContextHitPoints(): void {
    if (contextEnemyId !== null) {
        PostContextEnemyField(EnemyField.HitPoints, "contextHitPoints");
        return;
    }

    PostContextTracker(TrackerField.HitPoints, "contextHitPoints");
}

function PostContextDead(isDead: boolean): void {
    const nameSluggified = localStorage.getItem("nameSluggified");
    if (!nameSluggified) {
        return;
    }

    if (contextEnemyId !== null) {
        PostRoundWrite("enemy:dead:" + nameSluggified + ":" + contextEnemyId, "/Participant/SetEnemyDead", {
            slugName: nameSluggified,
            enemyId: contextEnemyId,
            isDead: String(isDead),
            roomKey: RoomKey()
        });
        return;
    }

    PostRoundWrite("dead:" + nameSluggified, "/Participant/SetDead", {
        slugName: nameSluggified,
        isDead: String(isDead),
        roomKey: RoomKey()
    });
}

function PostContextTracker(field: TrackerField, elementId: string): void {
    const nameSluggified = localStorage.getItem("nameSluggified");
    if (!nameSluggified) {
        return;
    }

    QueueTrackerUpdate(field, nameSluggified, ById<HTMLInputElement>(elementId).value);
}
